/* Progressive enhancement. Without JavaScript, variant links and Shopify forms remain native. */
(() => {
  'use strict';
  if (window.cicadaInitialized) return;
  window.cicadaInitialized = true;
  const rootURL = () => window.Shopify?.routes?.root || '/';
  const closeMenus = (restoreFocus = false) => {
    document.querySelectorAll('[data-mobile-menu][open]').forEach(menu => {
      menu.open = false;
      if (restoreFocus) menu.querySelector('summary').focus();
    });
  };
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') closeMenus(true);
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-mobile-menu]')) closeMenus();
    const discover = event.target.closest('[data-scroll-next]');
    const next = discover?.closest('.shopify-section')?.nextElementSibling;
    if (next && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      next.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
  });
  document.addEventListener('focusin', event => {
    if (!event.target.closest('[data-mobile-menu]')) closeMenus();
  });
  window.matchMedia('(min-width: 750px)').addEventListener('change', e => {
    if (e.matches) closeMenus();
  });
  const variantRequests = new WeakMap();
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-variant-link]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const root = link.closest('[data-product-root]');
    if (!root) return;
    event.preventDefault();
    variantRequests.get(root)?.abort();
    const controller = new AbortController();
    variantRequests.set(root, controller);
    const selectedURL = new URL(link.href);
    if (selectedURL.pathname !== location.pathname) { location.assign(selectedURL); return; }
    const destination = new URL(location.href);
    destination.searchParams.delete('variant');
    destination.searchParams.delete('option_values');
    for (const key of ['variant', 'option_values']) {
      if (selectedURL.searchParams.has(key)) destination.searchParams.set(key, selectedURL.searchParams.get(key));
    }
    const request = new URL(destination);
    request.searchParams.set('section_id', root.dataset.sectionId);
    root.setAttribute('aria-busy', 'true');
    root.querySelectorAll('[name="add"]').forEach(button => button.disabled = true);
    try {
      const response = await fetch(request, {signal: controller.signal});
      if (!response.ok) throw new Error('Variant could not be loaded.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-product-root]');
      if (!replacement) throw new Error('Missing product section.');
      root.replaceWith(replacement);
      history.replaceState({}, '', destination);
      replacement.querySelector(`[data-option-value-id="${link.dataset.optionValueId}"]`)?.focus({preventScroll: true});
      window.Shopify?.PaymentButton?.init();
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(destination);
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    if (!button || button.disabled) return;
    const message = form.querySelector('[data-product-error]');
    message.hidden = true;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    form.setAttribute('aria-busy', 'true');
    try {
      const data = new FormData(form);
      const response = await fetch(rootURL() + 'cart/add.js', {method: 'POST', headers: {'Accept': 'application/json'}, body: data});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'This item could not be added. Please try again.');
      location.assign(rootURL() + 'cart');
    } catch (error) {
      message.textContent = error instanceof TypeError ? 'We could not confirm the cart update. Check your cart before trying again.' : error.message;
      message.hidden = false;
      button.disabled = false;
      button.textContent = original;
      form.removeAttribute('aria-busy');
    }
  });
  // Validate quantity edits with change.js. Apply bottom to top so removing a
  // line cannot shift a pending line number, including duplicate variants.
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-cart-form]');
    if (!form) return;
    const changes = [...form.querySelectorAll('[data-quantity]')]
      .map((input, index) => ({input, line: index + 1, quantity: Number(input.value)}))
      .filter(({input, quantity}) => quantity !== Number(input.defaultValue)).reverse();
    if (!changes.length) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    const checkout = event.submitter?.name === 'checkout';
    const message = form.querySelector('[data-cart-error]');
    message.hidden = true;
    form.setAttribute('aria-busy', 'true');
    const buttons = [...form.querySelectorAll('button')];
    buttons.forEach(button => button.disabled = true);
    try {
      for (const {line, quantity} of changes) {
        if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Enter a whole-number quantity of zero or more.');
        const response = await fetch(rootURL() + 'cart/change.js', {
          method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({line, quantity})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'The quantity could not be changed.');
        if (quantity > 0 && result.items?.[line - 1]?.quantity !== quantity) throw new Error('Shopify adjusted this quantity to what is available.');
      }
      if (checkout) {
        const nativeForm = document.createElement('form');
        nativeForm.method = 'post'; nativeForm.action = rootURL() + 'cart';
        const field = document.createElement('input');
        field.type = 'hidden'; field.name = 'checkout'; field.value = 'Checkout';
        nativeForm.append(field); document.body.append(nativeForm); nativeForm.submit();
      } else location.reload();
    } catch (error) {
      message.textContent = (error instanceof TypeError ? 'We could not confirm this update.' : error.message) + ' Some changes may have been saved. ';
      const refresh = document.createElement('a');
      refresh.href = rootURL() + 'cart'; refresh.textContent = 'Refresh your cart to review quantities and totals before continuing.';
      message.append(refresh); message.hidden = false;
      form.removeAttribute('aria-busy');
      // Keep submit controls disabled until the current Shopify cart is reloaded.
    }
  });
  // Removal uses the exact line key, never the variant ID.
  document.addEventListener('click', async event => {
    const link = event.target.closest('[data-remove]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const form = link.closest('[data-cart-form]');
    if (form.getAttribute('aria-busy') === 'true') return;
    const message = form.querySelector('[data-cart-error]');
    message.hidden = true;
    form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(rootURL() + 'cart/change.js', {method: 'POST', headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify({id: link.dataset.key, quantity: 0})});
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This item could not be removed.');
      location.reload();
    } catch (error) {
      message.textContent = 'We could not confirm this change. Refresh your cart before trying again. ' + (error instanceof TypeError ? '' : error.message);
      message.hidden = false;
      form.removeAttribute('aria-busy');
    }
  });
  function setupInvitation() {
    const dialog = document.querySelector('[data-email-dialog]');
    if (!dialog || dialog.dataset.initialized || window.Shopify?.designMode) return;
    dialog.dataset.initialized = 'true';
    const key = 'cicada-email-invitation-dismissed';
    let dismissed = false;
    try { dismissed = Date.now() - Number(localStorage.getItem(key) || 0) < 7 * 86400000; } catch (_) { dismissed = true; }
    if (dismissed) return;
    let ready = false;
    let lastFocus;
    const dismiss = () => {
      try { localStorage.setItem(key, String(Date.now())); } catch (_) { /* Preferences only; no personal information. */ }
      dialog.close();
      lastFocus?.focus({preventScroll: true});
    };
    dialog.querySelectorAll('[data-dismiss-popup]').forEach(button => button.addEventListener('click', dismiss));
    dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
    const maybeOpen = () => {
      if (!ready || !dialog.isConnected || document.visibilityState !== 'visible') return;
      if (window.scrollY < (document.documentElement.scrollHeight - innerHeight) * 0.35) return;
      if (document.activeElement?.matches('input, textarea, select') || document.querySelector('[data-mobile-menu][open]')) return;
      ready = false;
      window.removeEventListener('scroll', maybeOpen);
      lastFocus = document.activeElement;
      dialog.showModal();
    };
    window.addEventListener('scroll', maybeOpen, {passive: true});
    setTimeout(() => { ready = true; maybeOpen(); }, Number(dialog.dataset.delay));
  }
  setupInvitation();
  document.addEventListener('shopify:section:load', () => { closeMenus(); setupInvitation(); });
})();
